/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injector, Input, NgZone, OnChanges, OnDestroy} from '@angular/core';
import {NgForm} from '@angular/forms';
import {Subscription} from 'rxjs';
import * as _ from 'lodash';
import {AppEventBusService} from '../events/app-event-bus.service';

import {UpdateSession} from '../../../components/session/update-session';
import {Uuid} from '../../../components/util/uuid';
import {bufferTemplateFlag as bufferSharedTemplateFlag, BufferedFlag} from '../util/template-state';
import {
  clearFormValidation,
  createFormSubmitState,
  FormControlError,
  FormSubmitState,
  handleApiValidationErrors,
  markFormSubmitError,
  markFormSubmitSuccess,
  updateFormWarnings
} from './form-submit-state';
import {applyResourceStateFields, applyResourceStateFromApi, wireModelCommsModelActions} from './model-comms-actions';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {AdnFormStateRegistration, AdnFormStateService} from '../../../components/util/adn-form-state.service';
import {AngularViewRefreshService} from '../util/angular-view-refresh.service';

function syncEditingModelDataToApiModel(editingModel: any, apiModel: any): void {
  // Notes are linked onto the API model from the notes panel; the editing copy
  // is often a disconnected clone and would wipe those links on the next save.
  const linkedNotes = apiModel?.notes;
  const linkedLineItemNotes = apiModel?.lineItemNotes;
  _.forEach(editingModel, (val, key) => {
    if (key.charAt(0) !== '$' && !_.isFunction(val)) {
      apiModel[key] = val;
    }
  });
  if ((!editingModel?.notes || !editingModel.notes.length) && linkedNotes?.length) {
    apiModel.notes = linkedNotes;
  }
  if ((!editingModel?.lineItemNotes || !editingModel.lineItemNotes.length)
    && linkedLineItemNotes?.length) {
    apiModel.lineItemNotes = linkedLineItemNotes;
  }
}

export interface AdminFormDirtyState {
  angularFormDirty: boolean;
  externalFormDirty: boolean;
  ngFormDirty: boolean;
  programmaticDirty: boolean;
}

export abstract class AdminResourceEditPageBase implements OnChanges, OnDestroy {
  static injector: Injector;
  @Input() model: any;

  private _modelCommsDirect: any;

  @Input()
  set modelComms(value: any) {
    this._modelCommsDirect = value;
  }

  get modelComms(): any {
    return this._modelCommsDirect;
  }

  private _isNewDirect: boolean;

  @Input()
  set isNew(value: boolean) {
    this._isNewDirect = value;
  }

  get isNew(): boolean {
    return this._isNewDirect;
  }

  editingModel: any = {};
  /** Mutable submit/validation state shared with SubmitCalls. */
  protected readonly submitState: FormSubmitState = createFormSubmitState();
  pending = false;
  externalFormDirty = false;

  private _registeredForm: NgForm | null = null;
  private _angularFormDirty = false;
  private _formSub: Subscription | null = null;
  private _formSettleTimer: any = null;
  private _formStateRegistration: (AdnFormStateRegistration & {programmaticDirty: boolean}) | null = null;
  private _initializingForm = false;
  private _resetting = false;
  private _modelCommsInitialized = false;
  private _deferFormDirtyTracking = false;
  private _userEditDuringInitialization = false;
  /** Template-stable copies of form flags that flip during async settle (avoids NG0100). */
  private readonly _validationWarningsFlag = {display: false, last: undefined as boolean | undefined};
  private readonly _validationErrorFlag = {display: false, last: undefined as boolean | undefined};
  private readonly _formPristineFlag = {display: true, last: undefined as boolean | undefined};
  private readonly _pendingFlag = {display: false, last: undefined as boolean | undefined};
  private readonly _saveDisabledFlag = {display: true, last: undefined as boolean | undefined};
  private readonly _validSubmissionFlag = {display: false, last: undefined as boolean | undefined};
  private readonly _isNewFlag = {display: false, last: undefined as boolean | undefined};

  private get _adnFormState(): any {
    return AdminResourceEditPageBase.injector?.get(AdnFormStateService, null);
  }

  private _registerFormState(): void {
    const formState = this._adnFormState;
    if (!formState) { return; }
    const self = this;
    const registration: AdnFormStateRegistration & {programmaticDirty: boolean} = {
      programmaticDirty: false,
      markDirty() {
        this.programmaticDirty = true;
        // Custom controls call AdnFormStateService.markDirty() from user event
        // handlers. Preserve that edit even while async form controls settle.
        self.markExternalFormDirty(true);
      },
      isDirty() {
        return !self._initializingForm
          && (this.programmaticDirty || !self.isFormPristine(self._registeredForm as NgForm));
      },
      resetSubmission() {
        self.submitState.validSubmission = false;
      }
    };
    this._formStateRegistration = registration;
    formState.setCurrentForm(registration);
  }

  protected formControlErrors: FormControlError[] = [];

  get validSubmission(): boolean {
    return this.submitState.validSubmission;
  }

  /**
   * Template binding for form-submission-message [validSubmission].
   * Success flips false→true after save while the form may still be verifying.
   */
  get displayValidSubmission(): boolean {
    return this.bufferTemplateFlag(
      this._validSubmissionFlag,
      !!this.submitState.validSubmission
    );
  }

  get apiErrors(): Array<{ key: string; apiText: string }> {
    return this.submitState.apiErrors;
  }

  get clientErrors(): any[] {
    return this.submitState.clientErrors;
  }

  get hasValidationWarnings(): boolean {
    // updateFormWarnings flips submitState on a timer that can overlap sibling
    // list HTTP settle / checkNoChanges (false→true NG0100 on *ngIf bindings).
    return this.bufferTemplateFlag(
      this._validationWarningsFlag,
      !!this.submitState.hasValidationWarnings
    );
  }

  get bulkError(): boolean | undefined {
    return this.submitState.bulkError;
  }

  get hasValidationError(): boolean {
    // Cleared/set during savePartial / submit settle (e.g. CPM fee modal) while
    // the edit page is still in checkNoChanges — true→false NG0100 on bindings.
    return this.bufferTemplateFlag(
      this._validationErrorFlag,
      this.submitState.validationError
    );
  }

  dismissSaveMessage(): void {
    this.submitState.validSubmission = false;
  }

  get registeredForm(): NgForm | null {
    return this._registeredForm;
  }

  protected constructor(
    protected eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) protected UserProfileSaver: any,
    @Inject(LocalNetworkProfile) protected LocalNetworkProfile: any,
    protected ngZone: NgZone
  ) {
  }

  ngOnDestroy(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
    this._adnFormState?.deregister();
  }

  ngOnChanges(): void {
    if (this.model && !this.modelComms) {
      this.resetEditingModel();
      return;
    }
    if (!this.model || !this.modelComms) {
      return;
    }
    if (!this._modelCommsInitialized) {
      this._modelCommsInitialized = true;
      wireModelCommsModelActions(this.modelComms, this.modelComms._resourceFactory);
      this.modelComms.addSubscriber((data, type) => {
        if (type || !data || !this.editingModel || this._resetting) {
          return;
        }
        this.ngZone.run(() => this.syncEditingModelFromExternal(data));
      });
    }
    this.resetEditingModel();
  }

  submit(editForm: NgForm): void {
    if (this.pending) {
      return;
    }

    this.pending = true;
    this.submitState.validSubmission = false;
    clearFormValidation(this.submitState, this.formControlErrors);

    this.onBeforeSubmit();

    if (!this.editingModel.id) {
      this.editingModel.id = Uuid.generate();
    }

    this.syncEditingModelToApiModel();
    const saveTarget = _.isFunction(this.model.savePartial) ? this.model : this.editingModel;

    saveTarget.savePartial(null, 'save', null).then((apiResource: any) => {
      this.ngZone.run(() => {
        // Clear dirty guard before applySave — afterSave hooks may navigate
        // (e.g. creative "to line item after save") and must not trip the modal.
        markFormSubmitSuccess(this.submitState, editForm);
        this.externalFormDirty = false;
        this._angularFormDirty = false;
        if (this._formStateRegistration) {
          this._formStateRegistration.programmaticDirty = false;
        }
        this._adnFormState?.deregister();

        this.applySave(apiResource);
        updateFormWarnings(this.submitState, this.editingModel, this.LocalNetworkProfile.isPureMarketplacePlatform());
        if (!UpdateSession.obtainData().authToken) {
          this.UserProfileSaver.saveObj(apiResource, 'adnLastEdited');
          this.UserProfileSaver.saveObj(apiResource, 'adnLastViewed');
        }

        // Stay-on-page saves: re-arm dirty tracking for subsequent edits.
        if (this._formStateRegistration) {
          this._adnFormState?.setCurrentForm(this._formStateRegistration);
        }
        this._startTrackingForm();
      });
    }, (response: any) => {
      if ((response.status === 400 || response.status === 403 || response.status === 422)
        && (response.data.type === 'ValidationResult' || response.data.type === 'ErrorMessage')) {
        handleApiValidationErrors(
          this.submitState,
          this.editingModel,
          _.get(response, ['data', 'errors']) || response.data,
          this.formControlErrors,
          editForm
        );
      }
      markFormSubmitError(this.submitState, editForm);
    }).finally(() => {
      this.pending = false;
      this.eventBus.emit('SUBMIT_PROCESSED');
    });
  }

  cancel(editForm: NgForm, extraDisabled = false): void {
    if (this.isSaveDisabled(editForm, extraDisabled)) {
      return;
    }
    this.resetEditingModel();
    this.submitState.validSubmission = false;
    this._angularFormDirty = false;
    editForm.control.markAsPristine();
    this._adnFormState?.deregister();
  }

  saveDirect(resource: any): void {
    _.merge(this.editingModel, resource);
    Object.assign(this.model, resource);
    this.isNew = false;
    this.afterInitAfterSave(resource);
    this.publishModelUpdate();
  }

  protected resetEditingModel(): void {
    this._resetting = true;
    this._userEditDuringInitialization = false;
    this.editingModel = _.cloneDeep(this.model);
    this.externalFormDirty = false;
    this._angularFormDirty = false;
    this.afterInitEditingModel();
    this.afterInitAfterSave();
    this.publishModelUpdate();
    this._resetting = false;
    this._startTrackingForm();
  }

  protected applySave(resource: any): void {
    if (resource) {
      _.merge(this.editingModel, resource);
      Object.assign(this.model, resource);
      applyResourceStateFromApi(this.editingModel, resource);
      applyResourceStateFromApi(this.model, resource);
    }
    this.isNew = false;
    // Publish before afterInitAfterSave so list queries cannot skip title/analysis updates.
    this.publishModelUpdate();
    this.afterInitAfterSave(resource);
  }

  /**
   * Push the editing model to modelComms (title crumbs, context actions, etc.)
   * and schedule Angular change detection for event-style subscribers.
   */
  protected publishModelUpdate(): void {
    this.modelComms?.modelUpdated(this.editingModel, this.isNew);
    this.eventBus?.runAsync();
  }

  protected syncEditingModelFromExternal(data: any): void {
    if (this.model) {
      applyResourceStateFields(this.editingModel, this.model);
    }
    applyResourceStateFields(this.editingModel, data);
    if (data?.objectState !== undefined) {
      this.editingModel.validationWarnings = _.cloneDeep(
        _.get(this.model, 'validationWarnings', data?.validationWarnings) || []
      );
    }
    const warningModel = this.getWarningModel();
    updateFormWarnings(this.submitState, warningModel, this.LocalNetworkProfile.isPureMarketplacePlatform());
  }

  protected onBeforeSubmit(): void {
  }

  protected syncEditingModelToApiModel(): void {
    syncEditingModelDataToApiModel(this.editingModel, this.model);
  }

  protected afterInitEditingModel(): void {
  }

  protected afterInitAfterSave(_saveObj?: any): void {
    void _saveObj;
  }

  registerForm(editForm: NgForm): void {
    if (this._registeredForm === editForm) { return; }
    this._registeredForm = editForm;
    this.submitState.ngForm = editForm;
    this._registerFormState();
    this._startTrackingForm();
    this.scheduleFormWarningsUpdate();
  }

  protected getWarningModel(): any {
    if (this.editingModel?.resourceName) {
      return this.editingModel;
    }
    return _.assign({}, this.editingModel, {resourceName: this.model?.resourceName});
  }

  protected scheduleFormWarningsUpdate(): void {
    // Match the resource form helper: apply API warning highlighting once controls exist.
    window.setTimeout(() => {
      this.ngZone.run(() => {
        updateFormWarnings(
          this.submitState,
          this.getWarningModel(),
          this.LocalNetworkProfile.isPureMarketplacePlatform()
        );
      });
    }, 100);
  }

  private _startTrackingForm(): void {
    this._formSub?.unsubscribe();
    clearTimeout(this._formSettleTimer);
    this._initializingForm = true;
    this._angularFormDirty = false;
    // Wait for child controls to finish their initial value writes before
    // subscribing, so those writes don't count as user edits.
    this._formSettleTimer = setTimeout(() => {
      if (!this._registeredForm || this._deferFormDirtyTracking) {
        return;
      }
      if (this._userEditDuringInitialization) {
        this._angularFormDirty = !!this._registeredForm.dirty;
        this.externalFormDirty = true;
      } else {
        this._registeredForm.control.markAsPristine();
        this._registeredForm.control.markAsUntouched();
        this.externalFormDirty = false;
        this._angularFormDirty = false;
        if (this._formStateRegistration) {
          this._formStateRegistration.programmaticDirty = false;
        }
      }
      this._initializingForm = false;
      this._userEditDuringInitialization = false;
      this._formSub = this._registeredForm.valueChanges?.subscribe(() => {
        // NgForm also emits when async controls receive programmatic values.
        // Only user-originated changes mark the form itself dirty.
        if (!this._angularFormDirty && this._registeredForm?.dirty) {
          this._angularFormDirty = true;
        }
      }) || null;
    }, 500);
  }

  markExternalFormDirty(userInitiated = false): void {
    if (this._initializingForm && !userInitiated) {
      return;
    }
    this._userEditDuringInitialization = this._initializingForm;
    if (this.externalFormDirty) {
      return;
    }
    // Defer past checkNoChanges — sync ngZone.run mid-CD flips formPristine
    // true→false (CPM fee modal save, programmatic field writes, etc.).
    setTimeout(() => {
      this.ngZone.run(() => {
        this.externalFormDirty = true;
        this.scheduleViewRefresh();
      });
    }, 0);
  }

  captureFormDirtyState(): AdminFormDirtyState {
    return {
      angularFormDirty: this._angularFormDirty,
      externalFormDirty: this.externalFormDirty,
      ngFormDirty: !!this._registeredForm?.dirty,
      programmaticDirty: !!this._formStateRegistration?.programmaticDirty
    };
  }

  restoreFormDirtyState(state: AdminFormDirtyState): void {
    this.ngZone.run(() => {
      this._angularFormDirty = state.angularFormDirty;
      this.externalFormDirty = state.externalFormDirty;
      if (this._registeredForm) {
        if (state.ngFormDirty) {
          this._registeredForm.control.markAsDirty();
        } else {
          this._registeredForm.control.markAsPristine();
        }
      }
      if (this._formStateRegistration) {
        this._formStateRegistration.programmaticDirty = state.programmaticDirty;
      }
    });
  }

  /**
   * Keep dirty tracking suppressed until async form sections finish loading.
   * Call resumeFormDirtyTracking() once those controls are in the DOM.
   */
  protected deferFormDirtyTracking(): void {
    this._deferFormDirtyTracking = true;
    this._initializingForm = true;
  }

  protected resumeFormDirtyTracking(): void {
    this._deferFormDirtyTracking = false;
    this._startTrackingForm();
  }

  isFormPristine(editForm: NgForm): boolean {
    void editForm;
    return !this._angularFormDirty && !this.externalFormDirty;
  }

  /**
   * Template binding for form-submission-message [formPristine].
   * Dirty flips during async settle must not race checkNoChanges (NG0100).
   */
  get displayFormPristine(): boolean {
    return this.bufferTemplateFlag(
      this._formPristineFlag,
      !this._registeredForm || this.isFormPristine(this._registeredForm as NgForm)
    );
  }

  /**
   * Template binding for [disabled]="pending" fieldsets.
   * Save settle sets pending true→false while the form may still be verifying.
   */
  get displayPending(): boolean {
    return this.bufferTemplateFlag(this._pendingFlag, !!this.pending);
  }

  /**
   * Template binding for isNew. First save sets isNew true→false while the
   * edit form may still be verifying (NG0100 on [disabled]/[isNew]/*ngIf).
   */
  get displayIsNew(): boolean {
    return this.bufferTemplateFlag(this._isNewFlag, !!this.isNew);
  }

  /** Buffer a boolean across CD so async hydrate cannot NG0100 *ngIf bindings. */
  protected bufferTemplateFlag(
    state: BufferedFlag,
    next: boolean
  ): boolean {
    return bufferSharedTemplateFlag(state, next, {
      onCommit: () => this.scheduleViewRefresh()
    });
  }

  private scheduleViewRefresh(): void {
    const refresh = AdminResourceEditPageBase.injector?.get(AngularViewRefreshService, null);
    if (refresh && typeof refresh.schedule === 'function') {
      refresh.schedule();
    }
  }

  isSaveDisabled(editForm: NgForm, extraDisabled = false): boolean {
    return extraDisabled
      || this.isFormPristine(editForm)
      || this.pending
      || editForm.pending
      || (editForm.invalid && !this.hasValidationError);
  }

  /**
   * Template binding for Save/Undo [disabled]. Imperative callers should use
   * isSaveDisabled() so buffered display lag cannot block cancel/submit logic.
   */
  displaySaveDisabled(editForm: NgForm, extraDisabled = false): boolean {
    return this.bufferTemplateFlag(
      this._saveDisabledFlag,
      this.isSaveDisabled(editForm, extraDisabled)
    );
  }
}
